import React, { createContext, useState, useContext, useEffect } from "react";
import { WandaUserInfo } from "../types/PersonTypes";
import {
  downloadContentImage,
  getFavoriteTripWithPagination,
  getServiceUsersInfo,
  getWandaOrganizationByName,
  getWandaTripWithPagination,
} from "../services/RegistrationService";
import * as AuthService from "../services/AuthService/AuthService";
import { notification } from "antd";
import { ITripDetails } from "../components/ServiceUsers/Interfaces/ITripDetails";
import {
  DefaultWandaTripInfo,
  IWandaServiceArea,
  IWandaTripInfo,
  TDriverInstruction,
  TTrip,
  bookingStatus,
} from "../types/TripTypes";
import {
  IServiceUserInfo,
  IWandaAccommodations,
} from "../types/ServiceUserInfo";
import { GeneralDefinition, WandaTripStatus } from "../types/ApplicationTypes";
import { Tabs } from "../types/TripPaginationFilterTypes";
import {
  DefaultWandaOrganizationDetails,
  WandaOrganizationDetails,
} from "../types/OrganizationTypes";
import { config } from "../types/CommonEnv";
import { usePersonSummaryCtx } from "./PersonSummaryContext";
import { base64ToBlob } from "../services";
import { useDashboardCtx } from "./DashboardContext";
import { getImageUrl } from "../helpers/personHelpers";

export const TripDetailsContext = createContext<any | undefined>(undefined);

export const useTripsCtx = () => useContext(TripDetailsContext);

const authService = AuthService;

const defaultDriverInstructionValue: TDriverInstruction = {
  pickup: false,
  dropOff: false,
};
export const TripDetailsProvider: React.FC = ({ children }) => {
  const [serviceData, setServiceData] = useState<IServiceUserInfo[]>([]);
  const [upcomingTripDetails, setUpcomingTripDetails] = useState<
    ITripDetails[]
  >([]);
  const [uspsFieldValidationError, setUspsFieldValidationError] = useState<
    TDriverInstruction
  >(defaultDriverInstructionValue);
  const [pastTripDetails, setPastTripDetails] = useState<ITripDetails[]>([]);
  const [favoriteTripDetails, setFavoriteTripDetails] = useState<
    ITripDetails[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApplyButtonClick, setIsApplyButtonClick] = useState<boolean>(false);
  const [rulesValidationId, setRulesValidationId] = useState<string>("");
  const [tripScheduleDate, setTripScheduleDate] = useState<string>("");
  const [tripCreationDetails, setTripCreationDetails] = useState<TTrip[]>([]);
  const [tripDate, setTripDate] = useState<string>("");
  const [wheelChair, setWheelChair] = useState<IWandaAccommodations>();
  const [wandaTripInfo, setWandaTripInfo] = useState<IWandaTripInfo>(
    DefaultWandaTripInfo
  );

  const [rebookTrip, setRebookTrip] = useState<TTrip[]>([]);
  const [isRebook, setIsRebook] = useState<boolean>(false);
  const [mapBase64, setMapBase64] = useState<string[]>([]);
  const [
    bookedTripPaymentInstrumentId,
    setBookedTripPaymentInstrumentId,
  ] = useState<number>();

  const [
    isModeSelectionBackBtnClicked,
    setIsModeSelectionBackBtnClicked,
  ] = useState<boolean>(false);
  const [wandaService, setWandaService] = useState<IWandaServiceArea[]>([]);
  const [isLoadingCostDetails, setIsLoadingCostDetails] = useState<boolean>(
    false
  );
  const [serviceUsersCount, setServiceUsersCount] = useState<number>(0);
  const [showOrderSummary, setShowOrderSummary] = useState<boolean>(true);
  const [totalServiceCount, setTotalServiceCount] = useState<{
    [tab: string]: number;
  }>({
    [Tabs.UPCOMING]: 0,
    [Tabs.PAST]: 0,
    [Tabs.FAVORITE]: 0,
  });

  const [wandaOrgId, setWandaOrgId] = useState<WandaOrganizationDetails>(
    DefaultWandaOrganizationDetails
  );
  const [isServiceUsersLoading, setIsServiceUsersLoading] = useState<boolean>(
    true
  );

  const { caregiverSummaryInfo } = usePersonSummaryCtx();

  const { selectedPersonIdCtx } = useDashboardCtx();
  const [, setSelectedPersonId] = selectedPersonIdCtx;

  useEffect(() => {
    if (caregiverSummaryInfo.personId) {
      getWandaOrganizationHierarchy();
    }
  }, [caregiverSummaryInfo.personId]);

  const getWandaOrganizationHierarchy = async () => {
    if (caregiverSummaryInfo.personId) {
      const organization = await getWandaOrganizationByName(
        config.organizationName,
        caregiverSummaryInfo.personId,
        ""
      );
      setWandaOrgId(organization);
    }
  };

  const getTripDetails = async (
    tripStatusId: number,
    rowsToSkip: number,
    fetchRows: number,
    fromDate: string,
    toDate: string,
    selectedPersonId: string,
    sortColumn: string,
    sortOrder: string
  ) => {
    setIsLoading(true);
    try {
      let tripDetails = await getWandaTripWithPagination({
        personId: caregiverSummaryInfo.personId,
        wandaTripStatusIds: tripStatusId,
        rowsToSkip,
        fetchRows,
        fromDate,
        toDate,
        selectedPersonId,
        sortColumn,
        sortOrder,
      });

      const trips = tripDetails.personTrips.map((trip: ITripDetails) => ({
        ...trip,
        statusDetail: bookingStatus[trip.tripStatusId],
      }));

      if (tripStatusId === WandaTripStatus.Booked) {
        setUpcomingTripDetails(trips);
        setTotalServiceCount((prev) => ({
          ...prev,
          [Tabs.UPCOMING]: tripDetails.totalTripCount,
        }));
      } else {
        setPastTripDetails(trips);
        setTotalServiceCount((prev) => ({
          ...prev,
          [Tabs.PAST]: tripDetails.totalTripCount,
        }));
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch trip details. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFavoriteTripDetails = async (
    rowsToSkip: number,
    fetchRows: number,
    fromDate: string,
    toDate: string,
    selectedPersonId: string,
    sortOrder: string
  ) => {
    setIsLoading(true);
    try {
      let trips = await getFavoriteTripWithPagination(
        caregiverSummaryInfo.personId,
        rowsToSkip,
        fetchRows,
        fromDate,
        toDate,
        selectedPersonId,
        sortOrder
      );
      setFavoriteTripDetails(
        trips.personTrips.map((data: ITripDetails, index: number) => ({
          ...data,
          id: index,
        }))
      );
      setTotalServiceCount((prev) => ({
        ...prev,
        [Tabs.FAVORITE]: trips.totalTripCount,
      }));
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Failed to fetch Favorite trip details. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  async function handleManageServiceUsersData() {
    try {
      setIsServiceUsersLoading(true);
      let serviceUserData = await getServiceUsersInfo(
        caregiverSummaryInfo.personId
      );
      setServiceUsersCount(serviceUserData.length);

      const promiseObjects = serviceUserData?.map(
        async (serviceUser: IServiceUserInfo) => {
          if (serviceUser.imageKey) {
            const profileImageUrl = await getImageUrl(serviceUser.imageKey);
            return {
              ...serviceUser,
              profileImageUrl,
            };
          }
          return serviceUser;
        }
      );

      const serviceUsers: IServiceUserInfo[] = await Promise.all(
        promiseObjects
      );

      setServiceData(serviceUsers);
      setSelectedPersonId(
        serviceUserData.length === 1 ? serviceUserData[0]?.personId : ""
      );
      return serviceUserData.length;
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Failed to fetch service user details. Please try again later.",
      });
    } finally {
      setIsServiceUsersLoading(false);
    }
  }

  return (
    <TripDetailsContext.Provider
      value={{
        serviceDataCtx: [serviceData, setServiceData],
        upcomingTripDetailsCtx: [upcomingTripDetails, setUpcomingTripDetails],
        pastTripDetailsCtx: [pastTripDetails, setPastTripDetails],
        favoriteTripDetailsCtx: [favoriteTripDetails, setFavoriteTripDetails],
        totalServiceCountCtx: [totalServiceCount, setTotalServiceCount],
        tripScheduleDateCtx: [tripScheduleDate, setTripScheduleDate],
        tripSetupDetailsCtx: [tripCreationDetails, setTripCreationDetails],
        isApplyButtonClickCtx: [isApplyButtonClick, setIsApplyButtonClick],
        uspsFieldValidationErrorCtx: [
          uspsFieldValidationError,
          setUspsFieldValidationError,
        ],
        modeSelectionBackBtnCtx: [
          isModeSelectionBackBtnClicked,
          setIsModeSelectionBackBtnClicked,
        ],
        tripValidationIdCtx: [rulesValidationId, setRulesValidationId],
        wandaTripInfoCtx: [wandaTripInfo, setWandaTripInfo],
        isLoadingCostDetailsCtx: [
          isLoadingCostDetails,
          setIsLoadingCostDetails,
        ],
        showOrderSummaryCtx: [showOrderSummary, setShowOrderSummary],
        rebookTripCtx: [rebookTrip, setRebookTrip],
        isRebookCtx: [isRebook, setIsRebook],
        tripDateCtx: [tripDate, setTripDate],
        wheelChairCtx: [wheelChair, setWheelChair],
        wandaServiceAreaCtx: [wandaService, setWandaService],
        mapBase64Ctx: [mapBase64, setMapBase64],
        bookedTripPaymentInstrumentIdCtx: [
          bookedTripPaymentInstrumentId,
          setBookedTripPaymentInstrumentId,
        ],
        isLoading,
        getWandaOrganizationHierarchy,
        getTripDetails,
        getFavoriteTripDetails,
        handleManageServiceUsersData,
        wandaOrgId,
        serviceUsersCount,
        isServiceUsersLoading,
      }}
    >
      {children}
    </TripDetailsContext.Provider>
  );
};
